import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Wallet, Bell, LogOut, Users, ScrollText, ChevronDown, Calculator, PackageSearch, Tag, ClipboardList, Banknote, ReceiptText } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';
import {
  Popover, PopoverTrigger, PopoverContent,
} from './ui/popover';
import { useAuth } from '../context/AuthContext';
import { listNotifications, markReadNotifications } from '../lib/api';
import { formatRp, MONTHS_ID } from '../lib/format';

export const AppHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState({ items: [], unread_count: 0 });
  const isAdmin = user?.role === 'admin' && !user?.opname_only;
  // Tombol switcher Buku Kas ↔ Stock Opname untuk kasir dengan hak opname
  const showKasirOpnameSwitcher =
    user?.role === 'kasir' && !!user?.opname_enabled;
  // Tombol switcher Sales Catalog ↔ Stock Opname untuk sales dengan hak opname
  const showSalesOpnameSwitcher =
    user?.role === 'sales' && !!user?.opname_enabled;
  // Tombol switcher Stock Opname ↔ Pajak Marketplace untuk user opname_only dgn hak pajak
  const showOpnamePajakSwitcher =
    !!user?.opname_only && !!user?.pajak_enabled;

  const refresh = async () => {
    if (!isAdmin) return;
    try {
      const r = await listNotifications({ limit: 20 });
      setNotifs(r);
    } catch (_) {}
  };

  useEffect(() => {
    if (!isAdmin) return;
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [isAdmin]); // eslint-disable-line

  const markAll = async () => {
    await markReadNotifications();
    refresh();
  };

  return (
    <header
      className="w-full bg-white border-b border-stone-200 sticky top-0 z-30"
      data-testid="app-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-3 flex items-center justify-between">
        <Link
          to={user?.home_route || (isAdmin ? '/admin' : '/kasir')}
          className="flex items-center gap-2 text-stone-900"
          data-testid="brand-link"
        >
          <div className="w-9 h-9 bg-emerald-900 flex items-center justify-center rounded-md">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-lg tracking-tight">MAINBULUTANGKIS <span className="text-lime-300">ERP</span></div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">
              Toko Main Bulutangkis
            </div>
          </div>
        </Link>

        {showKasirOpnameSwitcher && (
          <nav
            className="hidden sm:flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full p-1"
            data-testid="kasir-opname-switcher"
          >
            <NavLink
              to="/kasir"
              data-testid="switch-buku-kas"
              className={() => {
                const active = location.pathname.startsWith('/kasir');
                return `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-800 text-white shadow-sm'
                    : 'text-stone-600 hover:text-emerald-900'
                }`;
              }}
            >
              <Banknote className="w-3.5 h-3.5" /> Buku Kas
            </NavLink>
            <NavLink
              to="/stock-opname"
              data-testid="switch-stock-opname"
              className={() => {
                const active = location.pathname.startsWith('/stock-opname');
                return `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-800 text-white shadow-sm'
                    : 'text-stone-600 hover:text-emerald-900'
                }`;
              }}
            >
              <ClipboardList className="w-3.5 h-3.5" /> Stock Opname
            </NavLink>
          </nav>
        )}

        {showSalesOpnameSwitcher && (
          <nav
            className="hidden sm:flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full p-1"
            data-testid="sales-opname-switcher"
          >
            <NavLink
              to="/sales/catalog"
              data-testid="switch-sales-catalog"
              className={() => {
                const active = location.pathname.startsWith('/sales');
                return `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-800 text-white shadow-sm'
                    : 'text-stone-600 hover:text-emerald-900'
                }`;
              }}
            >
              <Tag className="w-3.5 h-3.5" /> Sales Catalog
            </NavLink>
            <NavLink
              to="/stock-opname"
              data-testid="switch-sales-stock-opname"
              className={() => {
                const active = location.pathname.startsWith('/stock-opname');
                return `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-800 text-white shadow-sm'
                    : 'text-stone-600 hover:text-emerald-900'
                }`;
              }}
            >
              <ClipboardList className="w-3.5 h-3.5" /> Stock Opname
            </NavLink>
          </nav>
        )}

        {showOpnamePajakSwitcher && (
          <nav
            className="hidden sm:flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full p-1"
            data-testid="opname-pajak-switcher"
          >
            <NavLink
              to="/stock-opname"
              data-testid="switch-opname-pill"
              className={() => {
                const active = location.pathname.startsWith('/stock-opname');
                return `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-800 text-white shadow-sm'
                    : 'text-stone-600 hover:text-emerald-900'
                }`;
              }}
            >
              <ClipboardList className="w-3.5 h-3.5" /> Stock Opname
            </NavLink>
            <NavLink
              to="/pajak"
              data-testid="switch-pajak-pill"
              className={() => {
                const active = location.pathname.startsWith('/pajak');
                return `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-800 text-white shadow-sm'
                    : 'text-stone-600 hover:text-emerald-900'
                }`;
              }}
            >
              <ReceiptText className="w-3.5 h-3.5" /> Pajak Marketplace
            </NavLink>
          </nav>
        )}

        <div className="flex items-center gap-2">
          {showKasirOpnameSwitcher && (
            // Mobile: kompak, tanpa label
            <div className="sm:hidden flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full p-1">
              <NavLink
                to="/kasir"
                data-testid="switch-buku-kas-mobile"
                className={() => {
                  const active = location.pathname.startsWith('/kasir');
                  return `p-1.5 rounded-full ${
                    active ? 'bg-emerald-800 text-white' : 'text-stone-600'
                  }`;
                }}
                aria-label="Buku Kas"
              >
                <Banknote className="w-4 h-4" />
              </NavLink>
              <NavLink
                to="/stock-opname"
                data-testid="switch-stock-opname-mobile"
                className={() => {
                  const active = location.pathname.startsWith('/stock-opname');
                  return `p-1.5 rounded-full ${
                    active ? 'bg-emerald-800 text-white' : 'text-stone-600'
                  }`;
                }}
                aria-label="Stock Opname"
              >
                <ClipboardList className="w-4 h-4" />
              </NavLink>
            </div>
          )}
          {showSalesOpnameSwitcher && (
            // Mobile: kompak, tanpa label
            <div className="sm:hidden flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full p-1">
              <NavLink
                to="/sales/catalog"
                data-testid="switch-sales-catalog-mobile"
                className={() => {
                  const active = location.pathname.startsWith('/sales');
                  return `p-1.5 rounded-full ${
                    active ? 'bg-emerald-800 text-white' : 'text-stone-600'
                  }`;
                }}
                aria-label="Sales Catalog"
              >
                <Tag className="w-4 h-4" />
              </NavLink>
              <NavLink
                to="/stock-opname"
                data-testid="switch-sales-stock-opname-mobile"
                className={() => {
                  const active = location.pathname.startsWith('/stock-opname');
                  return `p-1.5 rounded-full ${
                    active ? 'bg-emerald-800 text-white' : 'text-stone-600'
                  }`;
                }}
                aria-label="Stock Opname"
              >
                <ClipboardList className="w-4 h-4" />
              </NavLink>
            </div>
          )}
          {showOpnamePajakSwitcher && (
            // Mobile: kompak, tanpa label
            <div className="sm:hidden flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full p-1">
              <NavLink
                to="/stock-opname"
                data-testid="switch-opname-pill-mobile"
                className={() => {
                  const active = location.pathname.startsWith('/stock-opname');
                  return `p-1.5 rounded-full ${
                    active ? 'bg-emerald-800 text-white' : 'text-stone-600'
                  }`;
                }}
                aria-label="Stock Opname"
              >
                <ClipboardList className="w-4 h-4" />
              </NavLink>
              <NavLink
                to="/pajak"
                data-testid="switch-pajak-pill-mobile"
                className={() => {
                  const active = location.pathname.startsWith('/pajak');
                  return `p-1.5 rounded-full ${
                    active ? 'bg-emerald-800 text-white' : 'text-stone-600'
                  }`;
                }}
                aria-label="Pajak Marketplace"
              >
                <ReceiptText className="w-4 h-4" />
              </NavLink>
            </div>
          )}
          {isAdmin && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="relative border-stone-200"
                  data-testid="notif-bell-btn"
                >
                  <Bell className="w-4 h-4 text-stone-700" />
                  {notifs.unread_count > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                      data-testid="notif-unread-count"
                    >
                      {notifs.unread_count}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0 bg-white" data-testid="notif-popover">
                <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
                  <div className="font-heading font-semibold text-sm text-stone-900">
                    Notifikasi Saldo Awal
                  </div>
                  {notifs.unread_count > 0 && (
                    <button
                      onClick={markAll}
                      className="text-xs text-emerald-800 hover:underline"
                      data-testid="notif-mark-all"
                    >
                      Tandai baca
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.items.length === 0 && (
                    <div className="text-sm text-stone-500 py-6 text-center">
                      Belum ada notifikasi
                    </div>
                  )}
                  {notifs.items.map((n) => (
                    <div
                      key={n.id}
                      className={`px-4 py-3 border-b border-stone-100 last:border-b-0 ${n.read ? '' : 'bg-emerald-50/50'}`}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">
                          {n.kind === 'saldo_awal_created' ? 'Saldo Awal Diisi' : 'Saldo Awal Diubah'}
                        </span>
                        <span className="text-[10px] text-stone-500 num">
                          {new Date(n.timestamp).toLocaleString('id-ID')}
                        </span>
                      </div>
                      <div className="text-sm text-stone-800">
                        <span className="font-semibold">{n.actor_username}</span> di{' '}
                        <span className="font-semibold">{n.branch_name}</span> · {n.day} {MONTHS_ID[(n.month || 1) - 1]} {n.year}
                      </div>
                      <div className="text-xs text-stone-600 mt-1 num">
                        {n.kind === 'saldo_awal_updated'
                          ? `${formatRp(n.old_value)} → ${formatRp(n.new_value)}`
                          : formatRp(n.new_value)}
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-stone-700 hover:bg-stone-100"
                data-testid="user-menu-trigger"
              >
                <div className="w-7 h-7 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-900 text-xs font-bold flex items-center justify-center">
                  {(user?.full_name || user?.username || '?').charAt(0).toUpperCase()}
                </div>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-semibold text-stone-900">
                    {user?.full_name || user?.username}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">
                    {user?.role}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-white">
              <DropdownMenuLabel className="text-xs text-stone-500 font-normal">
                {user?.email || user?.username}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isAdmin && (
                <>
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                    <Wallet className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/kasir')} data-testid="menu-kasir">
                    <Wallet className="w-4 h-4 mr-2" /> Input Harian
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/users')} data-testid="menu-users">
                    <Users className="w-4 h-4 mr-2" /> Kelola Kasir
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/audit')} data-testid="menu-audit">
                    <ScrollText className="w-4 h-4 mr-2" /> Audit Log
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/jurnal')} data-testid="menu-jurnalku">
                    <Wallet className="w-4 h-4 mr-2 text-emerald-800" /> JurnalKu (Akuntansi)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/kalkulator')} data-testid="menu-kalkulator">
                    <Calculator className="w-4 h-4 mr-2 text-emerald-800" /> Kalkulator Marketplace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/produk-marketplace')} data-testid="menu-produk">
                    <PackageSearch className="w-4 h-4 mr-2 text-emerald-800" /> Daftar Produk
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/sales/catalog')} data-testid="menu-sales-catalog">
                    <Tag className="w-4 h-4 mr-2 text-emerald-800" /> Sales Price Catalog
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/pajak')} data-testid="menu-pajak">
                    <ReceiptText className="w-4 h-4 mr-2 text-emerald-800" /> Pajak Marketplace
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={logout}
                className="text-red-700 focus:text-red-800"
                data-testid="menu-logout"
              >
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
